import { supabase } from '@/supabase.config';
import type { Tithe, TitheFilters, TitheFormData, TithePaymentInput } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

const TITHE_SELECT = `
  *,
  authority_position:authority_positions!authority_position_id(
    id, org_type, district_id, region_id, role, started_at, ended_at,
    minister:ministers!minister_id(id, adb_code, full_name),
    district:districts!district_id(id, name, code),
    region:regions!region_id(id, name, code, district_id)
  ),
  tithe_district:districts!district_id(id, name, code),
  tithe_region:regions!region_id(id, name, code, district_id),
  payments:tithe_payments(
    id, tithe_id, method, amount, bank_account_id, check_number,
    bank_account:bank_accounts!bank_account_id(id, bank_name, account_name, account_number, currency, qr_code_url)
  )
`;

/**
 * Fetch all tithes for a given year (all subject types).
 * Caller reshapes into matrix.
 */
export async function getTithesByYear(filters: TitheFilters): Promise<Tithe[]> {
  const { data, error } = await db
    .from('tithes')
    .select(TITHE_SELECT)
    .eq('year', filters.year)
    .order('month', { ascending: true });
  if (error) throw error;
  return (data || []) as Tithe[];
}

/**
 * Fetch active authority positions for DISTRICT and/or NATIONAL org types.
 * DISTRICT scope uses org-level tithes (not positions). REGION and INSTITUTION also not position-based.
 * NATIONAL scope includes district authority positions (shown grouped under Directorio Nacional tab).
 */
export async function getActivePositionsForYear(year: number, filters: Pick<TitheFilters, 'scope'>) {
  if (filters.scope !== 'NATIONAL') return [];

  const jan1  = `${year}-01-01`;
  const dec31 = `${year}-12-31`;

  let query = db
    .from('authority_positions')
    .select(`
      id, org_type, district_id, region_id, role, started_at, ended_at,
      minister:ministers!minister_id(id, adb_code, full_name, dni),
      district:districts!district_id(id, name, code),
      region:regions!region_id(id, name, code, district_id)
    `)
    .lte('started_at', dec31)
    .or(`ended_at.is.null,ended_at.gte.${jan1}`);

  // NATIONAL tab shows national officers + all district officers grouped by district
  query = query.in('org_type', ['NATIONAL', 'DISTRICT']);

  query = query.order('org_type').order('district_id').order('role');

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

export interface DistrictEntry {
  id: string;
  name: string | null;
  code: string | null;
}

/** Fetch districts to display as org-level tithe rows (Distritos tab). */
export async function getDistrictsForTithes(
  filters: Pick<TitheFilters, 'scope'>
): Promise<DistrictEntry[]> {
  if (filters.scope !== 'DISTRICT') return [];

  const { data, error } = await db
    .from('districts')
    .select('id, name, code')
    .order('code');

  if (error) throw error;
  return (data || []) as DistrictEntry[];
}

export interface RegionEntry {
  id: string;
  name: string | null;
  code: string | null;
  district_id: string;
  district: { id: string; name: string | null; code: string | null } | null;
}

/** Fetch regions to display as org-level tithe rows (Regiones tab). */
export async function getRegionsForTithes(
  filters: Pick<TitheFilters, 'scope' | 'districtId' | 'regionId'>
): Promise<RegionEntry[]> {
  if (filters.scope !== 'REGION') return [];

  let query = db
    .from('regions')
    .select('id, name, code, district_id, district:districts!district_id(id, name, code)')
    .order('district_id')
    .order('code');

  if (filters.scope === 'REGION' && filters.regionId) {
    query = query.eq('id', filters.regionId);
  } else if (filters.districtId) {
    query = query.eq('district_id', filters.districtId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data || []) as RegionEntry[];
}

/** Record a new tithe payment (position, region, or institution) */
export async function createTithe(form: TitheFormData): Promise<string> {
  const row: Record<string, unknown> = {
    year:    form.year,
    month:   form.month,
    amount:  parseFloat(form.amount),
    paid_at: form.paid_at,
    notes:   form.notes || null,
    payer_name:             form.payer_name || null,
    amount_usd:             form.amount_usd ? parseFloat(form.amount_usd) : null,
    exchange_rate:          form.exchange_rate ? parseFloat(form.exchange_rate) : null,
    voucher_photo_url:      form.voucher_photo_url || null,
    voucher_transaction_ref: form.voucher_transaction_ref || null,
    voucher_depositor_name:  form.voucher_depositor_name || null,
    voucher_dest_account:    form.voucher_dest_account || null,
  };

  if (form.subjectType === 'position') {
    row.authority_position_id = form.subjectId;
  } else if (form.subjectType === 'district') {
    row.district_id = form.subjectId;
  } else if (form.subjectType === 'region') {
    row.region_id = form.subjectId;
  } else {
    row.institution_slug = form.subjectId;
  }

  const { data, error } = await db.from('tithes').insert(row).select('id').single();
  if (error) throw error;
  return (data as { id: string }).id;
}

export interface TitheReceiptFields {
  payer_name?: string | null;
  amount_usd?: number | null;
  exchange_rate?: number | null;
  voucher_photo_url?: string | null;
  voucher_transaction_ref?: string | null;
  voucher_depositor_name?: string | null;
  voucher_dest_account?: string | null;
}

/** Update an existing tithe (amount, paid_at, notes, and receipt/payment fields) */
export async function updateTithe(
  id: string,
  updates: {
    amount?: number;
    paid_at?: string;
    notes?: string | null;
  } & TitheReceiptFields
): Promise<void> {
  const { error } = await db
    .from('tithes')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

/**
 * Replaces every payment line on a tithe with the given set (full
 * delete-then-insert, since the form always submits its complete state
 * rather than incremental changes). A no-op amount/method line is dropped
 * silently — payment info stays optional overall.
 */
export async function replaceTithePayments(titheId: string, payments: TithePaymentInput[]): Promise<void> {
  const rows = payments
    .filter((p) => p.method && parseFloat(p.amount) > 0)
    .map((p) => ({
      tithe_id:        titheId,
      method:          p.method,
      amount:          parseFloat(p.amount),
      bank_account_id: p.bank_account_id || null,
      check_number:    p.check_number || null,
    }));

  const { error: delError } = await db.from('tithe_payments').delete().eq('tithe_id', titheId);
  if (delError) throw delError;
  if (rows.length === 0) return;

  const { error: insError } = await db.from('tithe_payments').insert(rows);
  if (insError) throw insError;
}

/** Delete a tithe record */
export async function deleteTithe(id: string): Promise<void> {
  const { error } = await db.from('tithes').delete().eq('id', id);
  if (error) throw error;
}

const POSITION_SELECT = `
  id, org_type, district_id, region_id, role, started_at, ended_at,
  minister:ministers!minister_id(id, adb_code, full_name),
  district:districts!district_id(id, name, code),
  region:regions!region_id(id, name, code, district_id)
`;

/** Fetch all data needed for a full Excel export (all scopes, all tabs). */
export async function fetchAllForExport(year: number) {
  const jan1  = `${year}-01-01`;
  const dec31 = `${year}-12-31`;

  const [tithesRes, posRes, distRes, regRes] = await Promise.all([
    db.from('tithes').select(TITHE_SELECT).eq('year', year).order('month', { ascending: true }),
    db.from('authority_positions')
      .select(POSITION_SELECT)
      .in('org_type', ['NATIONAL', 'DISTRICT'])
      .lte('started_at', dec31)
      .or(`ended_at.is.null,ended_at.gte.${jan1}`)
      .order('org_type').order('district_id').order('role'),
    db.from('districts').select('id, name, code').order('code'),
    db.from('regions')
      .select('id, name, code, district_id, district:districts!district_id(id, name, code)')
      .order('district_id').order('code'),
  ]);

  if (tithesRes.error) throw tithesRes.error;
  if (posRes.error)    throw posRes.error;
  if (distRes.error)   throw distRes.error;
  if (regRes.error)    throw regRes.error;

  return {
    tithes:    (tithesRes.data || []) as Tithe[],
    positions: posRes.data  || [],
    districts: (distRes.data || []) as DistrictEntry[],
    regions:   (regRes.data  || []) as RegionEntry[],
  };
}
